// movement-sense-info.ts — client-side render of the per-speed / per-sense
// breakdown panel, reading from window.FateTide.casterCache (no DB ping on a hit).
//
// Replaces options/tools/encounter_creatures/encounter_creature_info/
// movement_sense_info_inc.php. ONE generic panel keyed by a layer_variable:
//   - a speed (speed_walking … / teleport) → computed.creature.speeds.<short>
//   - a sense (nightsight … telepathy)       → computed.creature.senses.<name>
//   - blindsight_blind                        → a layer flag (active / inactive)
// Each speed/sense entry is the {total, sources} provenance the chips already read
// (layer_value → the same cluster), so the panel's number now MATCHES its chip.
//
// Behavior note vs the PHP: the catalog term's long item_description box is dropped
// (catalog text isn't in the caster cache). Term name comes from an embedded map;
// icon from iconMap(layerVariable) — the slug the chips use. Total + the per-source
// breakdown, the ½-walk default note, and the mount-suppression banner are kept.

import { renderSourceLineBlock } from './source-line-block';
import { setActiveInfoPanel, paintActiveInfoPanel } from './active-panel';
import type { CachedCaster, SourceRow, AcPart, ItemLikeRecord } from './types';

// variable_name → display name (additions.php SpeedType / Sense item_name).
const SPEED_NAMES: Record<string, string> = {
    speed_walking: 'Walking Speed',
    speed_jumping: 'Jumping Speed',
    speed_swimming: 'Swimming Speed',
    speed_climbing: 'Climbing Speed',
    speed_flying: 'Flying Speed',
    speed_burrowing: 'Burrowing Speed',
    speed_hovering: 'Hovering Speed',
    teleport: 'Teleport',
};
const SENSE_NAMES: Record<string, string> = {
    nightsight: 'Nightsight',
    blindsight: 'Blindsight',
    fiendsight: 'Fiendsight',
    ghostsight: 'Ghostsight',
    spellsight: 'Spellsight',
    truesight: 'Truesight',
    tremorsense: 'Tremorsense',
    telepathy: 'Telepathy',
};
const BLINDSIGHT_BLIND = 'blindsight_blind';

// StatusEffect riding / piloted (catalogs/StatusEffect.php) — these suppress
// non-teleport speeds, so the panel shows an explanatory banner for speed terms.
// `ridden` removed 2026-07-16: ridden mounts keep their own speeds (the compile
// no longer zeroes them), so the banner would be false on the mount.
const MOUNT_STATUS_IDS = [
    'FF8D219A-2A83-0EE5-A222-E748753E8D0B', // riding
    'DDC66C31-4093-84AF-18BB-093D924B8738', // piloted
];

function num(v: unknown): number {
    const n = Number(Array.isArray(v) ? v[0] : v);
    return Number.isFinite(n) ? n : 0;
}
function partTotal(part: AcPart | number | boolean | undefined): number {
    if (part === undefined || part === null) return 0;
    return (typeof part === 'object') ? num(part.total) : num(part);
}
function partSources(part: AcPart | number | boolean | undefined): SourceRow[] {
    return (part && typeof part === 'object' && Array.isArray(part.sources)) ? part.sources : [];
}

function isSpeed(lv: string): boolean {
    return lv === 'teleport' || lv.indexOf('speed_') === 0;
}
function speedShort(lv: string): string {
    return lv === 'teleport' ? 'teleport' : lv.slice('speed_'.length);
}

/** Extract a status_effect row's catalog item_id (bucketed shell, [0]-wrapped, or bare). */
function statusItemId(st: unknown): string {
    if (!st || typeof st !== 'object') return '';
    const o = st as ItemLikeRecord;
    const raw = o.items?.item_id ?? o.item_id;
    return String(Array.isArray(raw) ? raw[0] : (raw ?? ''));
}

function hasMountRestriction(caster: CachedCaster): boolean {
    const list = ftCreatureStatuses(caster, 'target');
    if (!Array.isArray(list)) return false;
    return list.some(st => MOUNT_STATUS_IDS.indexOf(statusItemId(st)) !== -1);
}

function renderMovementSensePanel(caster: CachedCaster, layerVariable: string): string {
    // blindsight_blind — active / inactive flag, no numeric breakdown.
    if (layerVariable === BLINDSIGHT_BLIND) {
        const active = !!num(ftLayerValue(caster, 'blindsight_blind')) || ftLayerValue(caster, 'blindsight_blind') === true;
        const badge = `<span class="badge ${active ? 'bg-warning' : 'bg-secondary'}">${active ? 'Active' : 'Inactive'}</span>`;
        return `<table class="table border">
            <thead><tr><th scope="col" colspan="2" class="table-success align-middle">${iconMap(BLINDSIGHT_BLIND)} Blindsight (Blind)</th></tr></thead>
            <tbody>
                <tr><th scope="row" class="align-middle">On this creature</th>
                    <td class="text-end"><h3 class="mb-0">${badge}</h3></td></tr>
            </tbody>
        </table>`;
    }

    const speed = isSpeed(layerVariable);
    const name = speed ? (SPEED_NAMES[layerVariable] ?? layerVariable) : (SENSE_NAMES[layerVariable] ?? layerVariable);
    const headerTint = speed ? 'table-info' : 'table-success';

    const cluster = speed
        ? ((caster.computed?.creature?.speeds as Record<string, AcPart | number> | undefined) ?? {})[speedShort(layerVariable)]
        : ((caster.computed?.creature?.senses as Record<string, AcPart | number> | undefined) ?? {})[layerVariable];

    let total = partTotal(cluster);
    let sources = partSources(cluster);

    // JUMPING is a pure client-side derivation (⅓ walking + 2·body, rounded up, min 10 ft),
    // so its breakdown renders for EVERY creature regardless of whether that creature's cached
    // blob was recompiled with the backend provenance rows. We defer to the backend's sources
    // ONLY when an explicit speed_jumping layer was authored — i.e. the cluster carries sources
    // and is NOT flagged `derived` (the derived recompute sets `derived: true`). Computing here
    // off computed.creature.speeds.walking.total (already status/mount-resolved) + the body stat
    // reproduces the backend's calculate_speed_jumping() exactly.
    let jumpingDerivedView = false;
    if (speed && speedShort(layerVariable) === 'jumping') {
        const clusterObj = (cluster && typeof cluster === 'object') ? cluster : null;
        const explicitAuthored = sources.length > 0 && clusterObj?.derived !== true;
        if (!explicitAuthored) {
            const walk = partTotal(caster.computed?.creature?.speeds?.walking);
            const bodyForJump = Math.max(0, Math.floor(num(ftLayerValue(caster, 'body'))));
            if (walk > 0) {
                const walkPart = Math.ceil(walk / 3);
                const bodyPart = 2 * bodyForJump;
                total = Math.max(10, walkPart + bodyPart); // == max(10, ceil(walk/3 + 2·body))
                const rows: SourceRow[] = [
                    { label: `⅓ × Walking Speed (${Math.round(walk)})`, value: walkPart, href_id: '', kind: 'speed_walking' },
                ];
                if (bodyPart !== 0) {
                    rows.push({ label: `2 ft × Body (${bodyForJump})`, value: bodyPart, href_id: '', kind: 'body' });
                }
                const baseSum = walkPart + bodyPart;
                if (baseSum < total) {
                    rows.push({ label: 'Minimum jump distance', value: total - baseSum, href_id: '', kind: 'speed_jumping' });
                }
                sources = rows;
                jumpingDerivedView = true;
            } else {
                // No walking speed → no jump (matches the backend's 0 case). Nothing to break down.
                total = 0;
                sources = [];
            }
        }
    }

    // Tinted title header (like the essence panels), then the full calculation: every
    // source line + a "Total" footer (icon + compiled value). Clicking the chip means the
    // viewer wants the whole breakdown, so we lead with it rather than a bare "Current" row.
    let html = `<table class="table border mb-2">
        <thead><tr><th scope="col" colspan="2" class="${headerTint} align-middle">${iconMap(layerVariable)} ${escapeHtml(name)}</th></tr></thead>
    </table>`;

    // Full breakdown — every contributing source plus the compiled Total (icon + value)
    // footer. With no sources (e.g. ½-walk-derived swim/climb) this is just the Total row.
    html += renderSourceLineBlock(sources, layerVariable, total, 'bg-secondary', caster);

    // Explanatory notes go BELOW the total. Rendered as a soft-blue box with light text so
    // they stay readable on the dark side panel (Bootstrap's alert-secondary was dark-on-dark).
    const note = (text: string): string =>
        `<div class="small rounded p-2 mt-2 text-start" role="note" style="color:#9ec5fe;background:rgba(110,168,254,0.10);border:1px solid rgba(110,168,254,0.28);text-align:left;">${text}</div>`;

    // Cluster normalization #3 (2026-06-10): the standalone default_swimming/_climbing flags
    // are gone — ½-walk derivation is `derived: true` ON the speed object (legacy cached
    // blobs may still carry a bare-number derived value: treat that shape as derived too).
    const speeds = caster.computed?.creature?.speeds ?? {};
    const speedIsDerived = (short: string): boolean => {
        const slot = speeds[short];
        if (slot === undefined || slot === null) return false;
        return typeof slot === 'object' ? slot.derived === true : true;
    };
    if (layerVariable === 'speed_swimming' && speedIsDerived('swimming')) {
        html += note('Swimming speed defaults to half your walking speed when no explicit swim speed is layered (see sheet logic for status-affected vs implicit values).');
    } else if (layerVariable === 'speed_climbing' && speedIsDerived('climbing')) {
        html += note('Climbing speed defaults to half your walking speed when no explicit climb speed is layered (see sheet logic for status-affected vs implicit values).');
    } else if (layerVariable === 'speed_jumping' && jumpingDerivedView) {
        html += note('Jumping speed is ⅓ of your walking speed plus 2 ft per point of Body, rounded up (minimum 10 ft). An explicit jump speed layer overrides this derivation.');
    }
    if (speed && hasMountRestriction(caster)) {
        html += note('Mount or pilot statuses (riding/ridden/piloted) suppress non-teleport speeds on the tactical sheet.');
    }

    return html;
}

/**
 * Entry point. Repointed onclick: the speed/sense chips in creature_summary_renderers.php
 * and table_item_player_character_inc.php. layerVariable is a SpeedType / Sense
 * variable_name, or 'blindsight_blind'.
 */
export async function renderMovementSenseInfoPanel(
    casterId: string,
    layerVariable: string,
    targetId = 'div_refresh_side_info',
): Promise<void> {
    const target = document.getElementById(targetId);
    if (!target) {
        return;
    }

    const lv = String(layerVariable ?? '').trim();
    const valid = lv === BLINDSIGHT_BLIND || lv in SPEED_NAMES || lv in SENSE_NAMES;
    if (!valid) {
        target.innerHTML = '<div class="p-3 text-center text-danger">Invalid movement or sense identifier.</div>';
        return;
    }

    let caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    if (!caster) {
        target.innerHTML = '<div class="p-3 text-center text-muted">Loading…</div>';
        try {
            await fetchAndCacheCaster(casterId);
        } catch {
            // fetchAndCacheCaster swallows its own errors; fall through to the miss guard.
        }
        caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    }

    if (!caster) {
        target.innerHTML = '<div class="p-3 text-center text-danger">Could not load creature data.</div>';
        return;
    }

    const render = (c: CachedCaster) => renderMovementSensePanel(c, lv);
    setActiveInfoPanel(casterId, targetId, render);
    paintActiveInfoPanel(caster);
}
